/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getAutoparksSelector,
	getIsAutoparksLoadingSelector,
	getPaginationSelector,
	getTableSearchSelector,
} from './selectors';
import { getAutoparksAction, resetStateAction } from './actions';
import { getIsAvailableAutoparksButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { getMenuByRules } from '../orders/utils';

const AutoparksContainerWrapper = ({
	onGetAutoparks,
	autoparks,
	isAutoparksLoading,
	pagination,
	onResetState,
	isAvailable,
}) => {
	useInjectSaga({ key: 'autoparksContainerSaga', saga });
	useInjectReducer({ key: 'autoparksContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetAutoparks({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'autopark');
		query.set('mode', 'add');
		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetAutoparks({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetAutoparks({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetAutoparks({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetAutoparks({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={getMenuByRules()}
			isInnerRouter
			pageContent={
				<DataGridComponent
					headerText="Автопарки"
					onAddButtonClick={isAvailable.update ? handleAddButtonClick : null}
					columns={TABLE_COLUMNS()}
					rows={autoparks}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isAutoparksLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	autoparks: getAutoparksSelector(),
	isAutoparksLoading: getIsAutoparksLoadingSelector(),
	pagination: getPaginationSelector(),
	tableSearch: getTableSearchSelector(),
	isAvailable: getIsAvailableAutoparksButtonsSelector(),
});

const mapDispatchToProps = {
	onGetAutoparks: getAutoparksAction,
	onResetState: resetStateAction,
};

const AutoparksContainer = connect(mapStateToProps, mapDispatchToProps)(AutoparksContainerWrapper);

AutoparksContainerWrapper.propTypes = {
	onGetAutoparks: PropTypes.func.isRequired,
	autoparks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			tk: PropTypes.shape({
				id: PropTypes.number.isRequired,
				name: PropTypes.string.isRequired,
			}).isRequired,
			name: PropTypes.string,
			link: PropTypes.string.isRequired,
		}),
	),
	isAutoparksLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default AutoparksContainer;
